import common from './common.js';
import frag from './shaders/pick.frag';
import IsoTile from './IsoTile';

export default class IsoWorld extends PIXI.Container {
	constructor(tileWidth = 44, tileHeight = 44) {
		super();

		this._tilesToUpdate = {
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
		};
		this._updateTileCounter = 0;
		this._tileUpdateCounter = 0;

		this.interactive = false;
		this.editorMode = false;

		this.waveFrequency = 0.7;

		this.signals = {
			select: new P3.utils.MiniSignal(),
			paint: new P3.utils.MiniSignal(),
			camerapan: new P3.utils.MiniSignal(),
		};

		this._tileset = null;
		this._structset = null;
		this._upgradeset = null;

		this._rows = 0;
		this._columns = 0;

		this._lastLeft = 0;
		this._lastRight = 0;
		this._lastTop = 0;
		this._lastBottom = 0;

		this._tileWidth = tileWidth;
		this._tileHeight = tileHeight;
		this._tiles = [];
		this._structureTiles = [];
		this._emptyTiles = [];
		this._tileColorMap = new Map();
		this._cheapestStructure = 999999;
		this._cheapestUpgrade = 999999;

		this._currentTile = null;
		this._previousTile = null;
		this._quakeScalar = 0;
		this._waveTime = 0;

		this._isWildfire = false;

		this._tileLayer = new PIXI.Container();
		this.addChild(this._tileLayer);

		this._interactionLayer = new PIXI.Container();
		this._interactionLayer.interactive = true;

		this._interactionLayer.layer = new PIXI.Graphics(); // need to add this or the interactionlayer does not work :(
		this._interactionLayer.layer.beginFill(0xFF0000, 1);
		this._interactionLayer.layer.drawRect(0, 0, P3.View.width, P3.View.height);
		this._interactionLayer.layer.endFill();
		this._interactionLayer.layer.alpha = 0;	// setting the whole graphic alpha rather than the parts alpha is better
		this._interactionLayer.addChild(this._interactionLayer.layer);

		this.addChild(this._interactionLayer);

		this._guiLayer = new PIXI.Container();
		this.addChild(this._guiLayer);

		this._tileSelect = new PIXI.Sprite(P3.assets.texture('tile_select'));
		this._tileSelect.scale.set(0.5);
		this._tileSelect.position.set(-1, 5);

		this._isRiskShowing = false;

		const holder = new PIXI.Container();

		this._pickLayer = new PIXI.Container();
		this._pickLayer.scale.set(0.2);
		holder.addChild(this._pickLayer);

		this._pickMapDirty = false;
		this._pickMapTexture = null;
		this._pickTileTexture = this.generatePickTile();
		this._pickOffset = new PIXI.Point();
		this._pickResolution = 0.5;
		this._pickGenerateFrame = 0;

		this._pickDebug = new PIXI.Sprite(PIXI.Texture.EMPTY);
		this._pickDebug.y = 200;
		this._pickDebug.alpha = 0.5;
		this._pickDebug.visible = false;
		this.addChild(this._pickDebug);

		this._cullingPadding = 300;

		this._camera = new P3.Camera(new PIXI.Point(), new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5));
		this._camera.addLayer(new P3.CameraLayer(this._tileLayer, new PIXI.Point(1, 1)));
		this._camera.addLayer(new P3.CameraLayer(this._guiLayer, new PIXI.Point(1, 1)));
		this._camera._firstRender = true;

		this._cameraDefaultPosition = new PIXI.Point(0, 0);
		this._cameraSpeed = 1000; // 740
		this._cameraEase = 0.2;
		this._cameraScroll = new PIXI.Point();
		this._cameraScrollOffset = null;
		this._cameraZoomLevel = -1;
		this._cameraZoomTween = null;
		this._cameraPan = null;
		this._cameraPanRadius = 14;
		this._cameraPanSensitivity = 2.4;
		this._cameraShakeStrength = new PIXI.Point(4, 1);
		this._cameraShakeScalar = 0;
		this._cameraShakeRate = 8;
		this._cameraShakeFrame = 0;
		this._cameraBounds = new PIXI.Rectangle();

		this._lastPointerPoint = new PIXI.Point();

		this._isCameraPan = false;
		this._isQuaking = false;

		this._tweens = new TWEEN.Group();
		this._quakeTween = null;

		this._interactionLayer.on('mousedown', this.onPointerDown, this);
		this._interactionLayer.on('mouseup', this.onPointerUp, this);
		this._interactionLayer.on('mouseupoutside', this.onPointerUp, this);
		this._interactionLayer.on('mousemove', this.onPointerMove, this);
		this._interactionLayer.on('touchstart', this.onPointerDown, this);
		this._interactionLayer.on('touchend', this.onPointerUp, this);
		this._interactionLayer.on('touchendoutside', this.onPointerUp, this);
		this._interactionLayer.on('touchmove', this.onPointerMove, this);
	}

	destroy() {
		this._interactionLayer.off('mousedown', this.onPointerDown, this);
		this._interactionLayer.off('mouseup', this.onPointerUp, this);
		this._interactionLayer.off('mouseupoutside', this.onPointerUp, this);
		this._interactionLayer.off('mousemove', this.onPointerMove, this);
		this._interactionLayer.off('touchstart', this.onPointerDown, this);
		this._interactionLayer.off('touchend', this.onPointerUp, this);
		this._interactionLayer.off('touchendoutside', this.onPointerUp, this);
		this._interactionLayer.off('touchmove', this.onPointerMove, this);

		this._tweens.removeAll();

		for (const group of this._tiles) {
			for (const tile of group) {
				tile.destroy();
			}
		}

		super.destroy({ children: true });
	}

	resize() {
		this._pickDebug.x = (common.STAGE_WIDTH - P3.View.width) * 0.5 + 10;
		this._interactionLayer.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
		this._interactionLayer.layer.width = P3.View.width;
		this._interactionLayer.layer.height = P3.View.height;
		this._camera._isMoving = true;

		if (!this.editorMode) {
			this._findBestZoom(1);
		}

		!this._camera._firstRender && this.renderTiles();
	}

	update(dt) {
		this.updateCamera(dt);

		if (this._camera._isMoving) {
			this.clearTileSelect();
		}

		if (!this.renderTiles()) {
			// only update visible
			for (let y = this._tilesToUpdate.top; y < this._tilesToUpdate.bottom; ++y) {
				for (let x = this._tilesToUpdate.left; x < this._tilesToUpdate.right; ++x) {
					const tile = this._tiles[y][x];

					tile.update(window.dt);
				}
			}
		}

		this._waveTime += this.waveFrequency * dt;
		this._tweens.update();
	}

	parseSets(tileset, structset, upgradeset) {
		this._tileset = P3.assets.json(tileset);
		this._structset = P3.assets.json(structset);
		this._upgradeset = P3.assets.json(upgradeset);

		for (const i in this._structset) {
			if (Object.hasOwn(this._structset, i)) {
				const structure = this._structset[i];

				if (structure.cost && (structure.type !== 2 && structure.type !== -1) && structure.cost < this._cheapestStructure) {
					this._cheapestStructure = structure.cost;
				}

				if (structure.group && structure.group !== structure.id) {
					const parent = this.getStructure(structure.group);

					for (const i in parent) {
						if (Object.hasOwn(parent, i)) {
							!structure[i] && (structure[i] = parent[i]);
						}
					}
				}
			}
		}
		for (const i in this._upgradeset) {
			if (Object.hasOwn(this._upgradeset, i)) {
				const upgrade = this._upgradeset[i];

				if (upgrade.cost && upgrade.cost < this._cheapestUpgrade) {
					this._cheapestUpgrade = upgrade.cost;
				}

				if (upgrade.group && upgrade.group !== upgrade.id) {
					const parent = this.getUpgrade(upgrade.group);

					for (const i in parent) {
						if (Object.hasOwn(parent, i)) {
							!upgrade[i] && (upgrade[i] = parent[i]);
						}
					}
				}
			}
		}
	}

	createMap(columns, rows, tileset, structset, upgradeset) {
		this._tileLayer.removeChildren();

		// set properties
		this._columns = Math.min(255, Math.max(10, columns));
		this._rows = Math.min(255, Math.max(10, rows));

		this.parseSets(tileset || 'tileset_ts_data', structset || 'structset_ts_data', upgradeset || 'upgradeset_ts_data');

		let i;

		for (i in this._tileset) {
			break;
		}
		const ground = this._tileset[i].id;

		this.clearMap();
		this.dirty();
		this.setCameraPosition(this._cameraDefaultPosition.x, this._cameraDefaultPosition.y);

		// create tiles
		for (let y = 0; y < this._rows; ++y) {
			this._tiles.push([]);
			for (let x = 0; x < this._columns; ++x) {
				const tile = new IsoTile(x, y, this);

				tile.position.set(x * this._tileWidth * 2 + y % 2 * this._tileWidth, y * this._tileHeight * 0.5);
				tile.init(ground);
				this._tiles[y].push(tile);
			}
		}

		// set tile neighbours
		for (let y = 0; y < this._rows; ++y) {
			for (let x = 0; x < this._columns; ++x) {
				const tile = this._tiles[y][x];

				tile.setNeighbours();
				if (common.selectedLevel === 'wildfire') {
					tile.calculateRiskCategory();
				}
			}
		}
	}

	loadMap(map) {
		this._tileLayer.removeChildren();

		// set properties
		this._columns = Math.min(255, map.columns);
		this._rows = Math.min(255, map.rows);

		this.parseSets(map.tileset, map.structset, map.upgradeset);

		this.clearMap();
		this.dirty();
		this.setCameraPosition((this._columns * 86) * 0.5, (this._rows * 52) * 0.2 + 54);

		this.setCameraZoomLevel(0, false);

		if (!this.editorMode) {
			this._cameraBounds.x = 54;
			this._cameraBounds.y = 36;
			this._cameraBounds.height = (this._rows * 52) * 0.4 + 54;
			this._cameraBounds.width = this._columns * 86 + 26;

			this._findBestZoom(1);
		}

		// create tiles
		for (let y = 0, rows = this._rows; y < rows; ++y) {
			for (let x = 0, columns = this._columns; x < columns; ++x) {
				this._tiles.push([]);
				const i = y * columns + x;
				const tile = new IsoTile(x, y, this);

				tile.position.set(x * this._tileWidth * 2 + y % 2 * this._tileWidth, y * this._tileHeight * 0.5);
				tile.init(map.data.ground[i], map.data.structure[i], map.data.level[i], { x, y });
				if (map.data.disabled[i]) {
					tile.disabled = true;
					tile.setGroundTexture();
				}
				this._tiles[y].push(tile);

				if (!this.addStructure(map.data.structure[i], tile) && !tile.isWater) {
					this._emptyTiles.push(tile);
				}
			}
		}

		// set tile neighbours
		for (let y = 0; y < this._rows; ++y) {
			for (let x = 0; x < this._columns; ++x) {
				const tile = this._tiles[y][x];

				tile.setNeighbours();
				if (common.selectedLevel === 'wildfire') {
					tile.calculateRiskCategory();
				}
			}
		}
		common.started = true;
		this.generatePickMap(false);
		this.updateTileSelect();
	}

	_findBestZoom(startZoom = 1) {
		const padding = 100;

		this._camera.zoom = startZoom;

		if ((P3.View.width / this._camera.zoom) + padding > this._cameraBounds.width) {
			this._findBestZoom(startZoom * 1.1);
		}

		return startZoom;
	}

	resizeMap(columns, rows) {
		if (columns === this._columns && rows === this._rows) {
			return;
		}

		// first tile
		let i;

		for (i in this._tileset) {
			break;
		}
		const ground = this._tileset[i].id;

		this.clearTileSelect();

		// crop
		if (columns < this._columns || rows < this._rows) {
			for (let y = this._rows - 1; y >= 0; --y) {
				for (let x = this._columns - 1; x >= 0; --x) {
					const tile = this._tiles[y][x];

					if (x > columns - 1 || y > rows - 1) {
						x > columns - 1 && this._tiles[y].splice(x, 1);
						tile.destroy();
					}
				}
				y > rows - 1 && this._tiles.splice(y, 1);
			}
		}

		// expand
		if (columns > this._columns || rows > this._rows) {
			for (let y = 0; y < rows; ++y) {
				y > this._rows - 1 && this._tiles.push([]);
				for (let x = 0; x < columns; ++x) {
					if (x > this._columns - 1 || y > this._rows - 1) {
						const tile = new IsoTile(x, y, this);

						tile.position.set(x * this._tileWidth * 2 + y % 2 * this._tileWidth, y * this._tileHeight * 0.5);
						tile.init(ground);
						this._tiles[y].push(tile);
					}
				}
			}
		}

		// set tile neighbours
		for (let y = 0; y < rows; ++y) {
			for (let x = 0; x < columns; ++x) {
				const tile = this._tiles[y][x];

				tile.setNeighbours();
				if (common.selectedLevel === 'wildfire') {
					tile.calculateRiskCategory();
				}
			}
		}
		this._columns = columns;
		this._rows = rows;
		this.dirty();

		// default camera position
		this.setCameraPosition(100, 100);
	}

	clearMap() {
		this.clearTileSelect();

		for (let y = 0, length = this._tiles.length; y < length; ++y) {
			for (let x = 0, length = this._tiles[y].length; x < length; ++x) {
				const tile = this._tiles[y][x];

				tile.destroy();
			}
		}
		this._tiles = [];
		this._structureTiles = [];
	}

	addStructure(id, tile) {
		if (id <= 0) {
			return false;
		}

		tile.structure = id;
		this._structureTiles.indexOf(tile) === -1 && this._structureTiles.push(tile);

		if (common.selectedLevel === 'wildfire' && tile.data.structure.group === 207) {
			if (tile.data.structure.orientation === 'left') {
				tile.playMineAnim('left');
			} else {
				tile.playMineAnim('right');
			}
		}

		if (common.started && tile.data.structure.group === 2) {
			common.achievements.unlockAchievement(0, 'tsunami');
		}

		return true;
	}

	removeStructure(tile) {
		if (tile.structure === 0) {
			return false;
		}

		tile.structure = 0;
		tile.data.upgrades = [];
		const i = this._structureTiles.indexOf(tile);

		(i !== -1) && this._structureTiles.splice(i, 1);

		return true;
	}

	createFirebreak(tile) {
		if (tile.structure === 0) {
			return;
		}
		let id = 0;
		const struct = this.getStructure(376);

		if (struct.group) {
			const random = [];

			for (const i in this.structset) {
				if (Object.hasOwn(this.structset, i)) {
					const t = this.structset[i];

					t.group === struct.group && random.push(t.id);
				}
			}
			id = P3.utils.selectRandom(random);
		}

		tile.structure = 0;
		this.addStructure(id, tile);
	}

	generatePickTile() {
		const rgb = new Float32Array(3);

		rgb[0] = 1;
		rgb[1] = 1;
		rgb[2] = 2;

		const tile = new PIXI.Sprite(P3.assets.texture('ground_ts_land_00'));

		tile.scale.set(0.5);
		tile.filters = [new PIXI.Filter(null, frag,	{ pickColor: rgb })];

		return common.renderer.generateTexture(tile, { scaleMode: PIXI.SCALE_MODES.NEAREST, resolution: 1 });
	}

	generatePickMap(debug = true) {
		if (!this._pickMapDirty) {
			return;
		}
		this._pickMapDirty = false;

		this._tileColorMap.clear();
		this._pickLayer.removeChildren();

		// create picking tiles
		for (let i = 0, length = this._tileLayer.children.length; i < length; ++i) {
			const tile = this._tileLayer.children[i];
			const color = new P3.Color();

			color.setRGB(1 - (tile.tx / 255), P3.math.randomInt(0, 255) / 255, 1 - (tile.ty / 255));
			this._tileColorMap.set(color.toHex(), tile);

			const pick = new PIXI.Sprite(this._pickTileTexture);

			pick.position.set(tile.x, tile.y);
			pick.tint = color.toNumber();
			this._pickLayer.addChild(pick);
		}

		// render to texture or clear
		if (this._tileLayer.children.length > 0) {
			const bounds = this._pickLayer.getBounds();

			this._pickMapTexture && this._pickMapTexture.destroy(true);
			this._pickMapTexture = common.renderer.generateTexture(this._pickLayer.parent, { scaleMode: PIXI.SCALE_MODES.NEAREST, resolution: this._pickResolution });
			this._pickOffset = new PIXI.Point(bounds.x, bounds.y);
		} else {
			common.renderer.renderTexture.bind(this._pickMapTexture);
			common.renderer.clear();
			common.renderer.renderTexture.bind(null);
		}

		if (debug) {
			this._pickDebug.texture = this._pickMapTexture;
			this._pickDebug.visible = true;
		}
	}

	dirty() {
		this._pickMapDirty = true;
	}

	renderTiles() {
		if (!this._camera._isMoving && !this._camera._firstRender) {
			return false;
		}

		this._camera._firstRender = false;

		this._tileLayer.removeChildren();

		const padding = this._cullingPadding;

		const left = Math.max(0, Math.round((this._camera.position.x - P3.View.width * 0.5 - padding) / (this._tileWidth * 2)));
		const right = Math.min(this._columns, Math.floor(((this._camera.position.x) + P3.View.width * 0.5 + padding) / (this._tileWidth * 2)));
		const top = Math.max(0, Math.floor((this._camera.position.y - P3.View.height * 0.5 - padding) / (this._tileHeight * 0.5)));
		const bottom = Math.min(this._rows, Math.floor(((this._camera.position.y) + P3.View.height * 0.5 + padding) / (this._tileHeight * 0.5)));

		this._tilesToUpdate.left = left;
		this._tilesToUpdate.right = right;
		this._tilesToUpdate.top = top;
		this._tilesToUpdate.bottom = bottom;

		(this._tilesToUpdate.left !== this._lastLeft
            || this._tilesToUpdate.right !== this._lastRight
            || this._tilesToUpdate.top !== this._lastTop
            || this._tilesToUpdate.bottom !== this._lastBottom) && this.dirty();

		this._lastLeft = this._tilesToUpdate.left;
		this._lastRight = this._tilesToUpdate.right;
		this._lastTop = this._tilesToUpdate.top;
		this._lastBottom = this._tilesToUpdate.bottom;

		// only render visible
		for (let y = this._tilesToUpdate.top; y < this._tilesToUpdate.bottom; ++y) {
			for (let x = this._tilesToUpdate.left; x < this._tilesToUpdate.right; ++x) {
				const tile = this._tiles[y][x];

				tile.update(window.dt);
				this._tileLayer.addChild(tile);
				this._guiLayer.addChild(tile.alert);
			}
		}

		// sort render order
		this._tileLayer.children.sort((a, b) => a.z - b.z);

		return true;
	}

	updateTileSelect() {
		if (!P3.input.mouse || this._pickMapDirty || PIXI.utils.isMobile.any || common.popupOpen) {
			return;
		}

		if (this._isCameraPan) {
			this.clearTileSelect();

			return;
		}

		const point = this._tileLayer.toLocal(P3.input.mouse, common.stage);
		const tile = this.selectTile((point.x * this._pickLayer.scale.x) - this._pickOffset.x,
			(point.y * this._pickLayer.scale.y) - this._pickOffset.y);

		if (tile) {
			tile.addChildAt(this._tileSelect, tile.getChildIndex(tile.structureSprite) - 1);
			if (this._currentTile !== tile) {
				P3.audio.playSound('sfx_tile_rollover_00');
			}
			this._currentTile = tile;
		} else {
			this.clearTileSelect();
		}
	}

	clearTileSelect() {
		this._currentTile = null;
		this._tileSelect.parent && this._tileSelect.parent.removeChild(this._tileSelect);
	}

	updateCamera(dt) {
		if ((this._cameraScroll.x - this._camera.target.x < 10 && this._cameraScroll.x - this._camera.target.x > -10)
            && (this._cameraScroll.y - this._camera.target.y < 10 && this._cameraScroll.y - this._camera.target.y > -10)) {
			this._camera._isMoving = false;
		} else {
			this._camera._isMoving = true;
		}

		if (!this._isCameraPan) {
			let diagonal = false;

			if (((P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) && (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT)))
                || ((P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) && (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT)))
                || ((P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) && (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT)))
                || ((P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) && (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT)))) {
				diagonal = true;
			}

			if (diagonal) {
				if ((P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) && (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT))) {
					this.scroll(3.6052402625905993, dt);
				}
				if ((P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) && (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT))) {
					this.scroll(-0.4636476090008061, dt);
				}
				if ((P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) && (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT))) {
					this.scroll(2.677945044588987, dt);
				}
				if ((P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) && (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT))) {
					this.scroll(0.4636476090008061, dt);
				}
			} else {
				if (P3.input.isKeyDown(P3.keys.W) || P3.input.isKeyDown(P3.keys.UP)) {
					this.scroll(-Math.PI * 0.5, dt);
				}
				if (P3.input.isKeyDown(P3.keys.A) || P3.input.isKeyDown(P3.keys.LEFT)) {
					this.scroll(-Math.PI, dt);
				}
				if (P3.input.isKeyDown(P3.keys.S) || P3.input.isKeyDown(P3.keys.DOWN)) {
					this.scroll(Math.PI * 0.5, dt);
				}
				if (P3.input.isKeyDown(P3.keys.D) || P3.input.isKeyDown(P3.keys.RIGHT)) {
					this.scroll(0, dt);
				}
			}
		}

		if (!this.editorMode) {
			if ((this._camera.target.x + (P3.View.width / this._camera.zoom) * 0.5) + 100 > this._cameraBounds.width) { // clamp X to maxwidth
				this._cameraScroll.x = this._cameraBounds.width - (P3.View.width / this._camera.zoom) * 0.5 - 102;
			}

			if ((this._camera.target.x - (P3.View.width / this._camera.zoom) * 0.5) - 100 < this._cameraBounds.x) { // clamp X to minwidth
				this._cameraScroll.x = this._cameraBounds.x + (P3.View.width / this._camera.zoom) * 0.5 + 102;
			}

			if ((this._camera.target.y + P3.View.height * 0.5) > this._cameraBounds.height) { // clamp Y to maxheight
				this._cameraScroll.y = this._cameraBounds.height - P3.View.height * 0.5 - 2;
			}

			if ((this._camera.target.y - P3.View.height * 0.5) < this._cameraBounds.y) { // clamp Y to minheight
				this._cameraScroll.y = this._cameraBounds.y + P3.View.height * 0.5 + 2;
			}
		}

		this._camera.target.x += (this._cameraScroll.x - this._camera.target.x) * this._cameraEase;
		this._camera.target.y += (this._cameraScroll.y - this._camera.target.y) * this._cameraEase;

		// camera shake
		if (++this._cameraShakeFrame % 2 === 0) {
			const shake = new PIXI.Point(
				P3.math.random(-this._cameraShakeStrength.x, this._cameraShakeStrength.x) * this._cameraShakeScalar,
				P3.math.random(-this._cameraShakeStrength.y, this._cameraShakeStrength.y) * this._cameraShakeScalar,
			);

			this._camera.offset.set(common.STAGE_WIDTH * 0.5 + shake.x, P3.View.height * 0.5 + shake.y);
		}

		this._camera.update();

		if (common.debug && P3.input.isKeyPressed(P3.keys.Q)) {
			this.setCameraZoomLevel(this._cameraZoomLevel - 1);
		}
		if (common.debug && P3.input.isKeyPressed(P3.keys.E)) {
			this.setCameraZoomLevel(this._cameraZoomLevel + 1);
		}

		if (this._isCameraPan) {
			const point = this._tileLayer.toLocal(P3.input.touch, common.stage);

			this._cameraScroll.set(
				this._cameraPan.x + this._cameraScrollOffset.x - point.x * this._cameraPanSensitivity,
				this._cameraPan.y + this._cameraScrollOffset.y - point.y * this._cameraPanSensitivity,
			);
		}
	}

	stopCameraPan() {
		this._isCameraPan = false;
		this._cameraPan = null;
		this._cameraScrollOffset = null;
	}

	setCameraPosition(x, y) {
		this._cameraScroll.set(x, y);
		this._camera.setTarget(this._cameraScroll.clone(), true);
	}

	setCameraZoomLevel(level = 1, animate = true) {
		let zoom;

		if (level === -99) {
			zoom = 1.6;
		} else {
			level = Math.min(2, Math.max(0, level));
			switch (level) {
			case 0 : zoom = 1; break;
			case 1 : zoom = 1.4; break;
			case 2 : zoom = 2; break;
			default: break;
			}
		}

		if (this._cameraZoomLevel !== level) {
			this._cameraZoomLevel = level;

			if (animate) {
				this._cameraZoomTween && this._cameraZoomTween.stop();
				this._cameraZoomTween = new TWEEN.Tween(this._camera, P3.scene.top.tweens)
					.to({ zoom }, 400)
					.easing(TWEEN.Easing.Quadratic.InOut)
					.onUpdate(() => {
						this._camera._isMoving = true;
						this.renderTiles();
					})
					.start();
			} else {
				this._camera.zoom = zoom;
			}
		}
	}

	startQuake(callback, scope = window) {
		if (this._isQuaking) {
			return;
		}
		this._isQuaking = true;

		const t = { value: 0 };

		this._quakeTween && this._quakeTween.stop();
		this._quakeTween = new TWEEN.Tween(t, this._tweens)
			.to({ value: 1 }, 600)
			.easing(TWEEN.Easing.Cubic.InOut)
			.onUpdate(() => {
				this._quakeScalar = this._cameraShakeScalar = t.value;
			})
			.onComplete(() => {
				callback && callback.call(scope);
			})
			.start();
	}

	stopQuake(callback, scope = window) {
		if (!this._isQuaking) {
			return;
		}

		const t = { value: 1 };

		this._quakeTween && this._quakeTween.stop();
		this._quakeTween = new TWEEN.Tween(t, this._tweens)
			.to({ value: 0 }, 1400)
			.easing(TWEEN.Easing.Quadratic.InOut)
			.onUpdate(() => {
				this._quakeScalar = this._cameraShakeScalar = t.value;
			})
			.onComplete(() => {
				this._isQuaking = false;
				callback && callback.call(scope);
			})
			.start();
	}

	scroll(angle, dt) {
		const v = new P3.Vector2(Math.cos(angle), Math.sin(angle));

		v.scaleBy(this._cameraSpeed * dt);
		this._cameraScroll.x += v.x;
		this._cameraScroll.y += v.y;

		this.signals.camerapan.dispatch(this);
	}

	selectTile(x, y) {
		if (!this._pickMapTexture) {
			return null;
		}

		common.renderer.renderTexture.bind(this._pickMapTexture);

		const resolution = this._pickMapTexture.baseTexture.resolution;
		const pixels = new Uint8Array(4);
		const gl = common.renderer.gl;

		gl.readPixels(
			x * resolution,
			y * resolution,
			1,
			1,
			gl.RGBA,
			gl.UNSIGNED_BYTE,
			pixels,
		);
		common.renderer.renderTexture.bind(null);

		const color = new P3.Color();

		color.setRGB(pixels[0] / 255, pixels[1] / 255, pixels[2] / 255);

		return this._tileColorMap.get(color.toHex());
	}

	toggleRisk() {
		this._isRiskShowing = !this._isRiskShowing;
		for (let y = 0, rows = this._rows; y < rows; ++y) {
			for (let x = 0, columns = this._columns; x < columns; ++x) {
				const tile = this.getTile(x, y);

				if (tile.isWater) {
					continue;
				}

				tile.riskSprite.visible = this._isRiskShowing;
			}
		}
	}

	screenToIso(x, y) {
		const w = this._tileWidth;
		const h = this._tileHeight * 0.5;

		return new PIXI.Point(
			(x / w + y / h) * 0.5,
			(y / h - (x / w)) * 0.5,
		);
	}

	isoToScreen(x, y) {
		const w = this._tileWidth;
		const h = this._tileHeight * 0.5;

		return new PIXI.Point(
			(x - y) * w,
			(x + y) * h,
		);
	}

	getTile(x, y) {
		if (y < 0 || y >= this._tiles.length) {
			return null;
		}
		if (x < 0 || x >= this._tiles[y].length) {
			return null;
		}

		return this._tiles[y][x];
	}

	getGround(id) {
		for (const i in this._tileset) {
			if (Object.hasOwn(this._tileset, i)) {
				const ground = this._tileset[i];

				if (ground.id === id) {
					return ground;
				}
			}
		}

		return null;
	}

	getGroundByName(name) {
		return this._tileset[name];
	}

	getStructure(id) {
		for (const i in this._structset) {
			if (Object.hasOwn(this._structset, i)) {
				const structure = this._structset[i];

				if (structure.id === id) {
					return structure;
				}
			}
		}

		return null;
	}

	getUpgrade(id) {
		for (const i in this._upgradeset) {
			if (Object.hasOwn(this._upgradeset, i)) {
				const upgrade = this._upgradeset[i];

				if (upgrade.id === id) {
					return upgrade;
				}
			}
		}

		return null;
	}

	getStructureByName(name) {
		return this._structset[name];
	}

	getStructureByGroup(group) {
		const result = [];

		for (const i in this._structset) {
			if (Object.hasOwn(this._structset, i)) {
				const structure = this._structset[i];

				if (structure.group && structure.group === group) {
					result.push(structure);
				}
			}
		}

		return result;
	}

	onPointerDown(event) {
		if (this.editorMode) {
			// this.generatePickMap(false);
			const point = event.data.getLocalPosition(this._tileLayer);
			const tile = this.selectTile(point.x * this._pickLayer.scale.x - this._pickOffset.x,
				point.y * this._pickLayer.scale.y - this._pickOffset.y);

			if (tile) {
				this.signals.paint.dispatch(this, tile);
			}
		} else {
			this.generatePickMap(false);
			this.updateTileSelect();
			this._lastPointerPoint.x = event.data.global.x;
			this._lastPointerPoint.y = event.data.global.y;

			this._cameraPan = event.data.getLocalPosition(this._tileLayer);
			this._cameraScrollOffset = new PIXI.Point(this._cameraScroll.x, this._cameraScroll.y);
		}
	}

	onPointerUp(event) {
		if (!this._isCameraPan) {
			const point = event.data.getLocalPosition(this._tileLayer);
			const tile = this.selectTile(point.x * this._pickLayer.scale.x - this._pickOffset.x,
				point.y * this._pickLayer.scale.y - this._pickOffset.y);

			if (tile) {
				this.generatePickMap(false);
				this.updateTileSelect();
				this.signals.select.dispatch(this, tile);
			}
		}
		this.stopCameraPan();
		this._mouseDown = false;
	}

	onPointerMove(event) {
		if (this.editorMode) {
			++this._pickGenerateFrame % 20 === 0 && this.generatePickMap(false);
			++this._updateTileCounter % 4 === 0 && this.updateTileSelect();
			const point = event.data.getLocalPosition(this._tileLayer);
			const tile = this.selectTile(point.x * this._pickLayer.scale.x - this._pickOffset.x,
				point.y * this._pickLayer.scale.y - this._pickOffset.y);

			if (tile) {
				P3.input.touch && this.signals.paint.dispatch(this, tile);
			}
		} else {
			if (!this._camera._isMoving) {
				++this._pickGenerateFrame % 20 === 0 && this.generatePickMap(false);
				++this._updateTileCounter % 4 === 0 && this.updateTileSelect();
			}
			if (this._cameraPan) {
				if (!this._isCameraPan) {
					const point = event.data.getLocalPosition(this._tileLayer);
					const v = new P3.Vector2(this._cameraPan.x - point.x, this._cameraPan.y - point.y);

					if (v.lengthSq > this._cameraPanRadius * this._cameraPanRadius) {
						this._cameraPan = new PIXI.Point(point.x * this._cameraPanSensitivity,
							point.y * this._cameraPanSensitivity);
						this._isCameraPan = true;
					}
				}
			}
			const distX = Math.abs(this._lastPointerPoint.x - event.data.global.x);
			const distY = Math.abs(this._lastPointerPoint.y - event.data.global.y);

			if (distX > 150 || distY > 150) {
				this.signals.camerapan.dispatch(this);
				this._lastPointerPoint.x = event.data.global.x;
				this._lastPointerPoint.y = event.data.global.y;
			}
		}
	}

	get peopleStructures() {
		return this.structureTiles.filter((v) => v.data.structure && v.data.structure.group === 350);
	}

	get structureTiles() {
		return this._structureTiles.slice();
	}

	get tileset() {
		return this._tileset;
	}

	get structset() {
		return this._structset;
	}

	get upgradeset() {
		return this._upgradeset;
	}

	get camera() {
		return this._camera;
	}

	get cameraZoomLevel() {
		return this._cameraZoomLevel;
	}

	get isCameraPan() {
		return this._isCameraPan;
	}

	get columns() {
		return this._columns;
	}

	get rows() {
		return this._rows;
	}

	get tileWidth() {
		return this._tileWidth;
	}

	get tileHeight() {
		return this._tileHeight;
	}

	get currentTile() {
		return this._currentTile;
	}

	get tileSelect() {
		return this._tileSelect;
	}

	get gui() {
		return this._guiLayer;
	}

	get waveTime() {
		return this._waveTime;
	}

	get quakeScalar() {
		return this._quakeScalar;
	}

	get isQuaking() {
		return this._isQuaking;
	}

	get isWildfire() {
		return this._isWildfire;
	}

	set isWildfire(v) {
		this._isWildfire = v;
	}

	get previousTile() {
		return this._previousTile;
	}

	set previousTile(v) {
		this._previousTile = v;
	}

	get cheapestItem() {
		return Math.min(this._cheapestStructure, this._cheapestUpgrade);
	}
}
